import React, {useEffect, useState, useRef} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { vehicleSlice, selectVehicleError } from "./../../store";
import { AuthService, VehicleService, EmployeeService, EventsService, computeDaysFromTodayToDate } from "../../services";
import { buildExternalDriverOptions, formatDriverDropdownLabel, isDriverEligibleEmployee, mergeDriverOptionsById } from "../../shared/utils/driverDropdownOptions";
import { Spinner, Breadcrumb, BreadcrumbItem, Tabs, Tab, Modal, Button } from "react-bootstrap";
import { Upload } from "react-bootstrap-icons";
import DatePicker from "react-datepicker";
import Select from 'react-select';
import moment from 'moment';
import {
  SEATING_CAPACITY_OPTIONS,
  FUEL_TYPE, FUEL_TYPE_TEXT,
  VEHICLE_TITLE, VEHICLE_TITLE_TEXT,
  LIFT_EQUIPPED, LIFT_EQUIPPED_TEXT,
  REPAIR_PART_NAME, REPAIR_PART_NAME_TEXT,
  QUANTITY_OPTIONS,
  useUnsavedChangesGuard,
  useFormDirtyTracker,
} from "../../shared";
import { findDuplicateVehicleByPlate } from "../../shared/utils/duplicateChecks";

const CreateVehicle = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { createVehicle } = vehicleSlice.actions;
  const error = useSelector(selectVehicleError);
  const [duplicateVehicleModalOpen, setDuplicateVehicleModalOpen] = useState(false);
  const pendingVehicleDataRef = useRef(null);

  // Basic Information
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [responsibleDriver, setResponsibleDriver] = useState(null);
  const [capacity, setCapacity] = useState('');
  const [mileage, setMileage] = useState('');
  const [make, setMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [year, setYear] = useState('');
  const [vin, setVin] = useState('');
  const [tag, setTag] = useState('');
  const [gps, setGps] = useState('');
  const [ezpass, setEzpass] = useState('');
  const [equipment, setEquipment] = useState('');
  const [hasLiftEquip, setHasLiftEquip] = useState('');
  const [fuelType, setFuelType] = useState('');
  const [title, setTitle] = useState('');
  const [titleOther, setTitleOther] = useState('');
  const [titleNumber, setTitleNumber] = useState('');

  // Compliance & Deadlines
  const [insuranceStartDate, setInsuranceStartDate] = useState(null);
  const [vehicleRegistrationDate, setVehicleRegistrationDate] = useState(null);
  const [emissionsDate, setEmissionsDate] = useState(null);

  // Check List
  const [checklist, setChecklist] = useState(['']);

  // Additional Information
  const [note, setNote] = useState('');

  // Drivers list
  const [drivers, setDrivers] = useState([]);

  // Documents
  const [selectedMonthlyFile, setSelectedMonthlyFile] = useState();
  const [selectedYearlyFile, setSelectedYearlyFile] = useState();
  const [monthlyInspectionDate, setMonthlyInspectionDate] = useState(null);
  const [yearlyInspectionDate, setYearlyInspectionDate] = useState(null);

  // Repair & Maintenance Record
  const [repairPartName, setRepairPartName] = useState('');
  const [repairReplacementDate, setRepairReplacementDate] = useState(null);
  const [repairMileage, setRepairMileage] = useState('');
  const [repairQuantity, setRepairQuantity] = useState('');
  const [repairCost, setRepairCost] = useState('');
  const [repairLocation, setRepairLocation] = useState('');
  const [repairReceiptFile, setRepairReceiptFile] = useState(null);
  const [repairNextReminder, setRepairNextReminder] = useState('');

  const { isDirty, resetBaseline } = useFormDirtyTracker({
    vehicleNumber,
    responsibleDriver: responsibleDriver?.value ?? '',
    capacity, mileage, make, vehicleModel, year, vin, tag, gps, ezpass, equipment,
    hasLiftEquip, fuelType, title, titleOther, titleNumber,
    insuranceStartDate, vehicleRegistrationDate, emissionsDate,
    checklist, note,
    selectedMonthlyFile: selectedMonthlyFile ? `${selectedMonthlyFile.name}|${selectedMonthlyFile.size}` : '',
    selectedYearlyFile: selectedYearlyFile ? `${selectedYearlyFile.name}|${selectedYearlyFile.size}` : '',
    monthlyInspectionDate, yearlyInspectionDate,
    repairPartName, repairReplacementDate, repairMileage, repairQuantity, repairCost,
    repairLocation,
    repairReceiptFile: repairReceiptFile ? `${repairReceiptFile.name}|${repairReceiptFile.size}` : '',
    repairNextReminder,
  });
  const { tryNavigate, tryGoBack } = useUnsavedChangesGuard(isDirty);

  const normalizeCurrencyInput = (value = '') =>
    String(value).replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
  const formatCurrencyDisplay = (value = '') => {
    const normalized = normalizeCurrencyInput(value);
    if (!normalized) return '';
    const amount = Number(normalized);
    if (!Number.isFinite(amount)) return '';
    return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  const normalizeMileageInput = (value = '') => String(value).replace(/[^0-9]/g, '');
  const formatMileageDisplay = (value = '') => {
    const normalized = normalizeMileageInput(value);
    if (!normalized) return '';
    return Number(normalized).toLocaleString();
  };

  useEffect(() => {
    if (!AuthService.canAddOrEditVechiles()) {
      window.alert('You haven\'t login yet OR this user does not have access to this page. Please change an Dispatcher or admin account to login.')
      AuthService.logout();
      navigate(`/login`);
    }
    Promise.all([
      EmployeeService.getAllEmployees(),
      EmployeeService.getExternalUserPermissionsList(EventsService.site),
    ])
      .then(([employeeResponse, externalPermissionResponse]) => {
        const employees = employeeResponse?.data || [];
        const internalDrivers = employees.filter(isDriverEligibleEmployee);
        const externalDrivers = buildExternalDriverOptions(externalPermissionResponse?.data || []);
        setDrivers(mergeDriverOptionsById(internalDrivers, externalDrivers));
      })
      .catch(() => {
        setDrivers([]);
      });
  }, []);

  const goBack = () => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('redirect') === 'schedule') {
      tryNavigate(`/trans-routes/schedule`);
      return;
    }
    tryGoBack('/vehicles/list');
  };

  const redirectAfterSave = () => {
    resetBaseline();
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get('redirect');
    if (redirect === 'schedule') {
      tryNavigate(`/trans-routes/schedule`);
    } else {
      tryNavigate('/vehicles/list');
    }
  };

  const addItemToArray = () => {
    const arr = [...checklist, ''];
    setChecklist(arr);
  }

  const formatDateForBackend = (date) => {
    if (!date) return '';
    return moment(date).format('MM/DD/YYYY');
  }

  const validateVehicle = () => {
    const errors = [];
    
    // Required fields validation
    if (!vehicleNumber || vehicleNumber.trim() === '') {
      errors.push('Vehicle Number');
    }
    if (!tag || tag.trim() === '') {
      errors.push('License Plate');
    }
    if (!vin || vin.trim() === '') {
      errors.push('VIN Number');
    }
    
    if (errors.length > 0) {
      window.alert(`Please fill in the following required fields:\n${errors.join('\n')}`);
      return false;
    }
    return true;
  };

  const saveVehicle = () => {
    if (!validateVehicle()) {
      return;
    }
    
    const data = {
      // Basic Information
      vehicle_number: vehicleNumber,
      responsible_driver: responsibleDriver?.label || '',
      responsible_driver_id: responsibleDriver?.value || '',
      capacity: parseInt(capacity) || 0,
      mileage: parseInt(mileage) || 0,
      make,
      vehicle_model: vehicleModel,
      year,
      vin,
      tag,
      gps_tag: gps,
      ezpass,
      equipment,
      has_lift_equip: hasLiftEquip === 'true',
      fuel_type: fuelType,
      title,
      title_other: titleOther,
      title_number: titleNumber,
      
      // Compliance & Deadlines
      insurance_start_date: formatDateForBackend(insuranceStartDate),
      vehicle_registration_date: formatDateForBackend(vehicleRegistrationDate),
      emissions_date: formatDateForBackend(emissionsDate),
      emission_test_on: formatDateForBackend(emissionsDate),
      
      // Check List
      checklist,
      
      // Additional Information
      note,
      
      // System fields
      status: 'active'
    };

    VehicleService.getAll()
      .then((res) => {
        const vehicles = res?.data || [];
        const dup = findDuplicateVehicleByPlate(vehicles, tag);
        if (dup) {
          pendingVehicleDataRef.current = data;
          setDuplicateVehicleModalOpen(true);
          return;
        }
        dispatch(createVehicle({ data, redirectFun: redirectAfterSave }));
      })
      .catch(() => {
        dispatch(createVehicle({ data, redirectFun: redirectAfterSave }));
      });
  }

  // Custom styles for react-select
  const selectStyles = {
    control: (baseStyles) => ({
      ...baseStyles,
      minWidth: '200px',
      borderRadius: '8px'
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    dropdownIndicator: (baseStyles) => ({
      ...baseStyles,
      paddingRight: '8px',
      color: '#333',
      '&:hover': {
        color: '#000',
      },
    }),
    valueContainer: (baseStyles) => ({
      ...baseStyles,
      height: '43px',
      padding: '0 8px',
    }),
    input: (baseStyles) => ({
      ...baseStyles,
      margin: '0px',
      padding: '0px',
      height: '30px',
      width: '290px'
    }),
    singleValue: (baseStyles) => ({
      ...baseStyles,
      margin: '0px',
    }),
  };

  return (
    <>
      <div className="list row mb-4">
        <Breadcrumb>
          <Breadcrumb.Item href="/trans-routes/dashboard">Transportation</Breadcrumb.Item>
          <Breadcrumb.Item href="/vehicles/list">
            Vehicles Information
          </Breadcrumb.Item>
          <Breadcrumb.Item active>
            Create New Vehicle
          </Breadcrumb.Item>
        </Breadcrumb>
        <div className="col-md-12 text-primary">
          <h4>Create New Vehicle <button className="btn btn-link btn-sm" onClick={() => {goBack()}}>Back</button></h4> 
        </div>
      </div>
      <div className="app-main-content-list-container form-page">
        <div className="app-main-content-list-func-container">
          <Tabs defaultActiveKey="basicInfo" id="vehicles-tab">
            <Tab eventKey="basicInfo" title="Basic Information">
              <h6 className="text-primary">Basic Information</h6>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Vehicle Number <span className="required">*</span></div>
                  <input type="text" placeholder="e.g., 101" value={vehicleNumber} onChange={e => setVehicleNumber(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">Responsible Driver</div>
                  <Select
                    value={responsibleDriver}
                    onChange={setResponsibleDriver}
                    options={[{value: '', label: ''}, ...drivers.map(driver => ({
                      value: driver?.id !== undefined && driver?.id !== null && driver?.id !== '' ? String(driver.id) : '',
                      label: formatDriverDropdownLabel(driver),
                    }))]}
                    styles={selectStyles}
                    placeholder="e.g., John Smith"
                    isClearable
                  />
                </div>
                <div className="me-4">
                  <div className="field-label">Seating Capacity</div>
                  <select value={capacity} onChange={e => setCapacity(e.target.value)}>
                    <option value="">Select...</option>
                    {SEATING_CAPACITY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="me-4">
                  <div className="field-label">Mileage</div>
                  <input type="number" placeholder="e.g., 48000" value={mileage} onChange={e => setMileage(e.target.value)}/>
                </div>
              </div>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Make</div>
                  <input type="text" placeholder="e.g., Ford" value={make} onChange={e => setMake(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">Model</div>
                  <input type="text" placeholder="e.g., T350" value={vehicleModel} onChange={e => setVehicleModel(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">Year</div>
                  <input type="text" placeholder="e.g., 2018" value={year} onChange={e => setYear(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">VIN Number <span className="required">*</span></div>
                  <input type="text" placeholder="e.g., 1FBAX2CM9KKA34959" value={vin} onChange={e => setVin(e.target.value)}/>
                </div>
              </div>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">License Plate <span className="required">*</span></div>
                  <input type="text" placeholder="e.g., 91579HT" value={tag} onChange={e => setTag(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">GPS ID</div>
                  <input type="text" placeholder="e.g., 609671" value={gps} onChange={e => setGps(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">E-ZPass</div>
                  <input type="text" placeholder="e.g., NY12345" value={ezpass} onChange={e => setEzpass(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">Lift Equipped</div>
                  <select value={hasLiftEquip} onChange={e => setHasLiftEquip(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(LIFT_EQUIPPED).map(key => (
                      <option key={key} value={LIFT_EQUIPPED[key]}>{LIFT_EQUIPPED_TEXT[LIFT_EQUIPPED[key]]}</option>
                    ))}
                  </select>
                </div>
                <div className="me-4">
                  <div className="field-label">Equipment</div>
                  <input
                    type="text"
                    placeholder="e.g., Dash Camera"
                    value={equipment}
                    onChange={(e) => setEquipment(e.target.value)}
                  />
                </div>
              </div>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Fuel Type</div>
                  <select value={fuelType} onChange={e => setFuelType(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(FUEL_TYPE).map(key => (
                      <option key={key} value={FUEL_TYPE[key]}>{FUEL_TYPE_TEXT[FUEL_TYPE[key]]}</option>
                    ))}
                  </select>
                </div>
                <div className="me-4">
                  <div className="field-label">Title</div>
                  <select value={title} onChange={e => setTitle(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(VEHICLE_TITLE).map(key => (
                      <option key={key} value={VEHICLE_TITLE[key]}>{VEHICLE_TITLE_TEXT[VEHICLE_TITLE[key]]}</option>
                    ))}
                  </select>
                </div>
                {title === 'other' && (
                  <div className="me-4">
                    <div className="field-label">Title (Other)</div>
                    <input type="text" placeholder="Please specify..." value={titleOther} onChange={e => setTitleOther(e.target.value)}/>
                  </div>
                )}
                <div className="me-4">
                  <div className="field-label">Title Number</div>
                  <input
                    type="text"
                    placeholder="123456"
                    value={titleNumber}
                    onChange={(e) => setTitleNumber(e.target.value)}
                  />
                </div>
              </div>

              <h6 className="text-primary">Check List</h6>
              <div className="app-main-content-fields-section column">
                {checklist.map((item, index) => (
                  <div className="mb-4" key={index}>
                    <input type="text" placeholder="e.g., Tire pressure" value={item} onChange={(e) => setChecklist([...checklist].map((a, index1) => {if (index1 === index) {return e.target.value;} return a;}))}/>
                    <button className="btn btn-link btn-sm" onClick={(e) => setChecklist([...checklist].filter((value, index1) => index1 != index))}>Remove</button>
                  </div>
                ))}
                <button className="btn btn-link" onClick={() => addItemToArray()}>+Add New Item</button>
              </div>

              <h6 className="text-primary">Additional Information</h6>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Notes and Attachments</div>
                  <textarea placeholder="e.g., Vehicle assigned to Route A" value={note} onChange={e => setNote(e.target.value)} rows={4} style={{width: '400px'}}/>
                </div>
              </div>

              {error && <div className="col-md-12 mb-4 alert alert-danger" role="alert">
                {error}
              </div>}
              <div className="list row mb-5">
                <div className="col-md-12 col-sm-12 col-xs-12">
                  <button className="btn btn-default btn-sm float-right" onClick={() => goBack()}> Cancel </button>
                  <button className="btn btn-primary btn-sm float-right" onClick={() => saveVehicle()}> Save </button>
                </div>
              </div>
            </Tab>

            <Tab eventKey="complianceDeadlines" title="Compliance & Deadlines">
              <h6 className="text-primary">Compliance & Deadlines</h6>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Insurance Expiration Date</div>
                  <DatePicker 
                    selected={insuranceStartDate} 
                    onChange={(date) => setInsuranceStartDate(date)}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="e.g., 03/01/2024"
                    className="form-control"
                  />
                </div>
                <div className="me-4">
                  <div className="field-label">Vehicle Registration Date</div>
                  <DatePicker 
                    selected={vehicleRegistrationDate} 
                    onChange={(date) => setVehicleRegistrationDate(date)}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="e.g., 03/01/2024"
                    className="form-control"
                  />
                </div>
                <div className="me-4">
                  <div className="field-label">Emissions</div>
                  <DatePicker
                    selected={emissionsDate}
                    onChange={(date) => setEmissionsDate(date)}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="03/01/2024"
                    className="form-control"
                  />
                </div>
              </div>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Insurance Remaining Days</div>
                  <input
                    type="text"
                    readOnly
                    className="form-control"
                    style={{ background: '#f5f5f5' }}
                    placeholder="e.g., 50"
                    value={
                      insuranceStartDate
                        ? computeDaysFromTodayToDate(formatDateForBackend(insuranceStartDate))
                        : ''
                    }
                  />
                  <small className="text-muted d-block">Days from today to insurance expiration date</small>
                </div>
                <div className="me-4">
                  <div className="field-label">Registration Remaining Days</div>
                  <input
                    type="text"
                    readOnly
                    className="form-control"
                    style={{ background: '#f5f5f5' }}
                    placeholder="e.g., 50"
                    value={
                      vehicleRegistrationDate
                        ? computeDaysFromTodayToDate(formatDateForBackend(vehicleRegistrationDate))
                        : ''
                    }
                  />
                  <small className="text-muted d-block">Days from today to registration date</small>
                </div>
                <div className="me-4">
                  <div className="field-label">Emissions Remaining Days</div>
                  <input
                    type="text"
                    readOnly
                    className="form-control"
                    style={{ background: '#f5f5f5' }}
                    placeholder="e.g., 50"
                    value={
                      emissionsDate
                        ? computeDaysFromTodayToDate(formatDateForBackend(emissionsDate))
                        : ''
                    }
                  />
                  <small className="text-muted d-block">Days from today to emissions date</small>
                </div>
              </div>

              {error && <div className="col-md-12 mb-4 alert alert-danger" role="alert">
                {error}
              </div>}
              <div className="list row mb-5">
                <div className="col-md-12 col-sm-12 col-xs-12">
                  <button className="btn btn-default btn-sm float-right" onClick={() => goBack()}> Cancel </button>
                  <button className="btn btn-primary btn-sm float-right" onClick={() => saveVehicle()}> Save </button>
                </div>
              </div>
            </Tab>

            <Tab eventKey="documents" title="Documents & Records">
              <h6 className="text-primary">Yearly Inspection</h6>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Yearly Inspection Date</div>
                  <DatePicker 
                    selected={yearlyInspectionDate} 
                    onChange={(date) => setYearlyInspectionDate(date)}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="e.g., 03/01/2024"
                    className="form-control"
                  />
                </div>
                <div className="me-4">
                  <div className="field-label">Yearly Inspection File</div>
                  <label className="custom-file-upload">
                    <Upload width={20} color={"#fff"} className="me-2"></Upload> Upload
                    <input type="file" onChange={(e) => setSelectedYearlyFile(e.target.files[0])}/>
                  </label>
                  <div className="file-name">{selectedYearlyFile?.name}</div>
                </div>
              </div>

              <h6 className="text-primary">Monthly Inspection</h6>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Monthly Inspection Date</div>
                  <DatePicker 
                    selected={monthlyInspectionDate} 
                    onChange={(date) => setMonthlyInspectionDate(date)}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="e.g., 03/01/2024"
                    className="form-control"
                  />
                </div>
                <div className="me-4">
                  <div className="field-label">Monthly Inspection File</div>
                  <label className="custom-file-upload">
                    <Upload width={20} color={"#fff"} className="me-2"></Upload> Upload
                    <input type="file" onChange={(e) => setSelectedMonthlyFile(e.target.files[0])}/>
                  </label>
                  <div className="file-name">{selectedMonthlyFile?.name}</div>
                </div>
              </div>

              <h6 className="text-primary">Repair & Maintenance Record</h6>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Part Name<span className="required">*</span></div>
                  <select value={repairPartName} onChange={e => setRepairPartName(e.target.value)}>
                    <option value="">Select...</option>
                    {Object.keys(REPAIR_PART_NAME).map(key => (
                      <option key={key} value={REPAIR_PART_NAME[key]}>{REPAIR_PART_NAME_TEXT[REPAIR_PART_NAME[key]]}</option>
                    ))}
                  </select>
                </div>
                <div className="me-4">
                  <div className="field-label">Replacement Date<span className="required">*</span></div>
                  <DatePicker 
                    selected={repairReplacementDate} 
                    onChange={(date) => setRepairReplacementDate(date)}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="e.g., 03/01/2024"
                    className="form-control"
                  />
                </div>
                <div className="me-4">
                  <div className="field-label">Mileage at Replacement</div>
                  <input
                    type="text"
                    placeholder="e.g., 48,000"
                    value={repairMileage}
                    onFocus={(e) => setRepairMileage(normalizeMileageInput(e.target.value))}
                    onChange={(e) => setRepairMileage(normalizeMileageInput(e.target.value))}
                    onBlur={(e) => setRepairMileage(formatMileageDisplay(e.target.value))}
                  />
                </div>
                <div className="me-4">
                  <div className="field-label">Quantity</div>
                  <select value={repairQuantity} onChange={e => setRepairQuantity(e.target.value)}>
                    <option value="">Select...</option>
                    {QUANTITY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="app-main-content-fields-section">
                <div className="me-4">
                  <div className="field-label">Total Cost<span className="required">*</span></div>
                  <input
                    type="text"
                    placeholder="e.g., $250.00"
                    value={repairCost}
                    onFocus={(e) => setRepairCost(normalizeCurrencyInput(e.target.value))}
                    onChange={(e) => setRepairCost(normalizeCurrencyInput(e.target.value))}
                    onBlur={(e) => setRepairCost(formatCurrencyDisplay(e.target.value))}
                  />
                </div>
                <div className="me-4">
                  <div className="field-label">Location<span className="required">*</span></div>
                  <input type="text" placeholder="e.g., Rockville Auto Center" value={repairLocation} onChange={e => setRepairLocation(e.target.value)}/>
                </div>
                <div className="me-4">
                  <div className="field-label">Next Replacement Reminder</div>
                  <input
                    type="text"
                    placeholder="e.g., 78,000"
                    value={repairNextReminder}
                    onFocus={(e) => setRepairNextReminder(normalizeMileageInput(e.target.value))}
                    onChange={(e) => setRepairNextReminder(normalizeMileageInput(e.target.value))}
                    onBlur={(e) => setRepairNextReminder(formatMileageDisplay(e.target.value))}
                  />
                </div>
                <div className="me-4">
                  <div className="field-label">Receipt Upload</div>
                  <label className="custom-file-upload">
                    <Upload width={20} color={"#fff"} className="me-2"></Upload> Upload
                    <input type="file" onChange={(e) => setRepairReceiptFile(e.target.files[0])}/>
                  </label>
                  <div className="file-name">{repairReceiptFile?.name}</div>
                </div>
              </div>

              {error && <div className="col-md-12 mb-4 alert alert-danger" role="alert">
                {error}
              </div>}
              <div className="list row mb-5">
                <div className="col-md-12 col-sm-12 col-xs-12">
                  <button className="btn btn-default btn-sm float-right" onClick={() => goBack()}> Cancel </button>
                  <button className="btn btn-primary btn-sm float-right" onClick={() => saveVehicle()}> Save </button>
                </div>
              </div>
            </Tab>
          </Tabs>
        </div>
      </div>

      <Modal
        show={duplicateVehicleModalOpen}
        onHide={() => {
          pendingVehicleDataRef.current = null;
          setDuplicateVehicleModalOpen(false);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Possible duplicate vehicle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          A vehicle already exists with the same license plate. Do you want to create another record anyway?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              pendingVehicleDataRef.current = null;
              setDuplicateVehicleModalOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              const dataToSend = pendingVehicleDataRef.current;
              pendingVehicleDataRef.current = null;
              setDuplicateVehicleModalOpen(false);
              if (dataToSend) {
                dispatch(createVehicle({ data: dataToSend, redirectFun: redirectAfterSave }));
              }
            }}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreateVehicle;
